import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaMusic, FaGlobe, FaStar, FaShieldAlt, FaChevronRight, FaPhone, FaEnvelope } from "react-icons/fa";
import "./StudentLife.css";

const sections = [
  {
    id: "saem",
    label: "SAEM",
    icon: <FaMusic />,
    fullName: "Sampradaya Academy of Ethnomusicology",
    tagline: "Music as a Path to Holistic Development",
    heroImage: "/images/saem-music.jpg",
    intro:
      "The Sampradaya Academy of Ethnomusicology (SAEM) is an Indian Classical music education initiative by Sri Vani Educational and Cultural Trust (SVECT), designed to enhance cognitive development and personality in school children through musical training. SAEM blends the contemporary Suzuki method with traditional guru-kula teaching systems, using associational memory exercises, pictorial representations, and audio-visual aids.",
    programmes: [
      {
        title: "Vocal Music",
        desc: "Students are trained in Carnatic Classical, Hindustani Classical, Folk, and Light Music — building a deep connection with India's rich vocal traditions from an early age.",
        image: "/images/saem-music.jpg",
        items: ["Carnatic Classical Music", "Hindustani Classical Music", "Folk Music", "Light Music"],
      },
      {
        title: "Instrumental Music",
        desc: "A wide range of instruments is taught by experienced faculty, encouraging students to find their voice through melody and rhythm.",
        image: "/images/saem-dance.jpg",
        items: ["Tabla", "Violin", "Flute", "Veena", "Harmonium", "Mridangam", "Guitar", "Keyboard"],
      },
      {
        title: "Classical Dance",
        desc: "Classical dance forms are offered as a medium of cultural expression, physical discipline, and artistic growth.",
        image: "/images/saem-logo.jpg",
        items: ["Kathak", "Bharatanatyam", "Odissi", "Kuchipudi"],
      },
    ],
    highlights: [
      "Open to Sri Vani students and external participants",
      "Both group and one-on-one sessions available",
      "Merit-based advancement opportunities",
      "Available across all four campuses",
    ],
    contact: { phone: "+91-96069 45236", email: "saem@srivani.org" },
  },
  {
    id: "vedavignana",
    label: "Vedavignana",
    icon: <FaStar />,
    fullName: "Sri Jnanashakthi Subrahmanyaswami Prabhodini Varadashrama Swamy Pathashala",
    tagline: "A New Paradigm Of Education",
    heroImage: "/images/vedavignana-main.jpg",
    intro:
      "Vedavignana is Sri Vani's most distinctive educational initiative, reviving ancient Gurukula traditions centred on Sanatana Dharma and Vedic studies. Students receive ~4.5 hours of Vedic learning from Sanskrit scholars daily, combined with ~2.5 hours of CBSE academic coursework — a rare dual-curriculum that grounds young minds in both ancient wisdom and modern knowledge.",
    programmes: [
      {
        title: "Veda Patha",
        desc: "Dedicated study of the Chaturveda Samhita — among humanity's oldest literary works — taught daily by qualified Sanskrit scholars in the traditional Gurukula format.",
        image: "/images/vedavignana-pavanje-1.jpg",
        items: ["Chaturveda Samhita", "Sanskrit instruction", "Traditional recitation methods", "Daily 4.5-hour sessions"],
      },
      {
        title: "Modern Academics",
        desc: "Alongside Vedic studies, students follow a structured CBSE-aligned curriculum affiliated with the National Institute of Open Schooling (NIOS), with formal exams at Grades 8, 9, and 10.",
        image: "/images/vedavignana-pavanje-2.jpg",
        items: ["CBSE-standard coursework", "NIOS affiliation", "Exams at Grades 8, 9 & 10", "Dual certification"],
      },
      {
        title: "Residential Life",
        desc: "The semi-residential programme provides breakfast, lunch, and evening snacks. Future plans include transformation into a fully residential facility on 2.5 acres of acquired land.",
        image: "/images/vedavignana-temple.jpg",
        items: ["Semi-residential setup", "All meals provided", "Over 30 active students", "No discrimination of any kind"],
      },
    ],
    highlights: [
      "~4.5 hours Vedic learning + ~2.5 hours CBSE daily",
      "Affiliated with National Institute of Open Schooling (NIOS)",
      "Breakfast, lunch & evening snacks provided",
      "Over 30 students currently enrolled",
      "2.5 acres acquired for future residential campus",
    ],
    location: "Pavanje, Dakshina Kannada — classes at Shri Jnanashakthi Subrahmanya Swami Temple",
    contact: { phone: "+91-94838 14168", email: "ask@srivani.org" },
  },
  {
    id: "c4e",
    label: "Centre for Excellence",
    icon: <FaGlobe />,
    fullName: "Sri Vani Centre for Excellence (C4e)",
    tagline: "Dream Bigger. Aim Higher. Go Global.",
    heroImage: "/images/c4e-main.jpg",
    heroPos: "75% 15%",
    intro:
      "The Sri Vani Centre for Excellence (C4e) is the brainchild of Shri Sharada Prasad RH. It functions as a bridge between nations, fostering enriching international experiences and partnerships with schools globally — including Pembroke Hill School in Kansas City and institutions in Slovenia and Eastern Europe. C4e is a step towards dreaming bigger, aiming higher and going global.",
    programmes: [
      {
        title: "Teacher Enrichment Program (STEP)",
        desc: "High school teachers interact with industry leaders, learn cutting-edge research, and develop professional connections. Twelve teachers from three Sri Vani branches visited Pembroke Hill School, Kansas City during 2014–15.",
        image: "/images/c4e-step.jpg",
        items: ["Industry leader interactions", "International school visits", "Cutting-edge research exposure", "Professional networking opportunities"],
      },
      {
        title: "Youth Leadership Program",
        desc: "Character-building outdoor experiences for students aged 13–17, including camping trips, social responsibility projects, field science training, and public speaking development.",
        image: "/images/c4e-youth-1.jpg",
        items: ["Camping & outdoor experiences", "Social responsibility projects", "Field science training", "Public speaking development"],
      },
      {
        title: "Capacity Building",
        desc: "Structured programmes addressing goal-setting, group dynamics, team building, persuasive communication, conflict resolution, and social service to develop student leaders.",
        image: "/images/c4e-capacity.jpg",
        items: ["Goal-setting workshops", "Group dynamics & team building", "Persuasive communication", "Conflict resolution & social service"],
      },
      {
        title: "Teachers Roundtable",
        desc: "A platform connecting industry leaders, government experts, and non-profit representatives with teachers to explore cutting-edge technologies and global resources.",
        image: "/images/c4e-roundtable.jpg",
        items: ["Industry & government experts", "Non-profit partnerships", "Cutting-edge technology exposure", "Global resource exchange"],
      },
      {
        title: "Incubation Centre",
        desc: "A collaborative lab fostering professional collaboration among students and teachers with organisations to develop life skills through arts, theatre, and music.",
        image: "/images/c4e-incubation.jpg",
        items: ["Arts, theatre & music", "Student–teacher–industry collaboration", "Life skills development", "Creative & professional growth"],
      },
      {
        title: "Cultural Exchange Program",
        desc: "Students interact with diverse cultural, religious, geographic, and socioeconomic backgrounds. A 2017 Peru programme included Machu Picchu, Amazonian jungle exploration, and Andean music sessions.",
        image: "/images/c4e-cultural.jpg",
        items: ["International cultural immersions", "Peru — Amazon & Machu Picchu (2017)", "Himalayan trek — Harkidhun Valley (2016)", "NASA Space Camp (2011)"],
      },
      {
        title: "Internship for Youth Leaders",
        desc: "Merit-based 3–6 week internships with prestigious organisations, enabling students to work alongside accomplished professionals and build real-world skills.",
        image: "/images/c4e-internship.jpg",
        items: ["3–6 week merit-based internships", "Work with accomplished professionals", "Real-world skill building", "Mentorship from industry leaders"],
      },
      {
        title: "Create, Participate & Propagate",
        desc: "Supporting student social entrepreneurs in developing action plans and implementing projects post-graduation — turning ideas into impact.",
        image: "/images/c4e-create.jpg",
        items: ["Social entrepreneurship support", "Action plan development", "Post-graduation project implementation", "Community impact initiatives"],
      },
    ],
    highlights: [
      "NASA Space Camp attendance — 2011",
      "Amazon Research Centre visit in Peru — 2017",
      "Himalayan trek to Harkidhun Valley — 2016",
      "12 teachers visited Pembroke Hill School, Kansas City",
      "Partnerships across USA, Slovenia & Eastern Europe",
    ],
    contact: { email: "ask@srivani.org" },
  },
  {
    id: "ncc-scouts",
    label: "NCC / Scouts",
    icon: <FaShieldAlt />,
    fullName: "National Cadet Corps & Scouts",
    tagline: "Discipline. Service. Leadership.",
    heroImage: "/images/young-gandhiyan-award.jpg",
    intro:
      "Sri Vani's NCC and Scouts programmes instil discipline, leadership, and a spirit of national service in students. Through structured training, camps, and community activities, students develop character and civic responsibility that extends beyond the classroom.",
    programmes: [
      {
        title: "Programme Details Coming Soon",
        desc: "Full programme information for NCC and Scouts will be updated shortly. Please contact the school office for current enrolment details.",
        image: "/images/young-gandhiyan-award.jpg",
        items: ["Contact school office for enrolment", "Details to be updated soon"],
      },
    ],
    highlights: [
      "Programme details will be updated soon",
      "Contact us for current enrolment information",
    ],
    contact: { phone: "+91-80-2332 2239", email: "ask@srivani.org" },
    placeholder: true,
  },
];

const StudentLife = () => {
  const [active, setActive] = useState("saem");
  const current = sections.find((s) => s.id === active);

  return (
    <div className="student-life-page">
      <section className="page-header">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1>Student Life</h1>
            <p>Beyond the classroom — programmes that shape the whole person</p>
          </motion.div>
        </div>
      </section>

      {/* Tab nav */}
      <div className="sl-tabs-bar">
        <div className="container">
          <div className="sl-tabs">
            {sections.map((s) => (
              <button
                key={s.id}
                className={`sl-tab ${active === s.id ? "active" : ""}`}
                onClick={() => setActive(s.id)}
              >
                <span className="sl-tab-icon">{s.icon}</span>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
        >
          {/* Hero image banner */}
          <section className="sl-hero">
            <div className="sl-hero-image-wrap">
              <img
                src={current.heroImage}
                alt={current.fullName}
                className="sl-hero-img"
                style={{ objectPosition: current.heroPos || "center 30%" }}
              />
              <div className="sl-hero-overlay">
                <div className="container">
                  <div className="sl-hero-icon-circle">{current.icon}</div>
                  <h2 className="sl-hero-title">{current.fullName}</h2>
                  <p className="sl-hero-tagline">"{current.tagline}"</p>
                </div>
              </div>
            </div>
          </section>

          {/* Intro + Highlights */}
          <section className="sl-intro-section section">
            <div className="container sl-intro-grid">
              <div className="sl-intro-text">
                <h3>About</h3>
                <p>{current.intro}</p>
                {current.location && <p className="sl-location">📍 {current.location}</p>}
              </div>
              <div className="sl-intro-highlights">
                <h3>Highlights</h3>
                <ul className="sl-highlights-list">
                  {current.highlights.map((h, i) => (
                    <li key={i}>
                      <FaChevronRight className="sl-bullet" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Programmes — alternating image + text */}
          <section className="sl-programmes-section">
            {current.programmes.map((prog, i) => (
              <motion.div
                key={i}
                className={`sl-prog-row ${i % 2 === 1 ? "reverse" : ""}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5 }}
              >
                <div className="sl-prog-image">
                  <img src={prog.image} alt={prog.title} />
                </div>
                <div className="sl-prog-content">
                  <h3>{prog.title}</h3>
                  <p>{prog.desc}</p>
                  <ul className="sl-prog-list">
                    {prog.items.map((item, j) => (
                      <li key={j}>
                        <FaChevronRight className="sl-bullet-sm" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </section>

          {/* Contact strip */}
          <section className="sl-contact-strip">
            <div className="container sl-contact-inner">
              <span>For enquiries about {current.label}:</span>
              <div className="sl-contact-links">
                {current.contact.phone && (
                  <a href={`tel:${current.contact.phone}`}>
                    <FaPhone /> {current.contact.phone}
                  </a>
                )}
                {current.contact.email && (
                  <a href={`mailto:${current.contact.email}`}>
                    <FaEnvelope /> {current.contact.email}
                  </a>
                )}
              </div>
            </div>
          </section>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default StudentLife;
